import type { GlobalFilters, GovernanceCommandView, QueryResult } from '@aurora-pvi/contracts';
import { ROUTES } from '@aurora-pvi/contracts';
import { daysBetween, round } from '@aurora-pvi/seed';
import { PAGE_SOURCES, type RepositoryContext, resolved } from '../context';
import { applyFilters } from '../filters';
import { buildMetadata, metricValue } from '../metadata';

/**
 * Governance command centre (ref: 10_…png).
 *
 * The queues, exceptions and audit rows are all counted from real records, so a
 * steward who clears a queue in a future iteration will see the number move
 * rather than watching a constant.
 */

export function createGovernanceRepository(context: RepositoryContext) {
  const { dataset, index, user } = context;

  return {
    async getCommandCenter(filters: GlobalFilters): Promise<QueryResult<GovernanceCommandView>> {
      const scope = applyFilters(dataset, index, user, filters);

      const pendingAssessments = dataset.assessments.filter(
        (a) => a.status === 'Submitted' || a.status === 'InReview',
      ).length;
      const pendingDecisions = dataset.decisions.filter(
        (d) => d.status === 'Submitted' || d.status === 'InReview',
      ).length;
      const draftMappingSets = dataset.mappingSets.filter((m) => m.status === 'Draft').length;
      const openConflicts = dataset.sourceConflicts.filter((c) => c.status === 'Open').length;
      const openIssues = dataset.dataQualityIssues.filter((i) => i.status === 'Open').length;
      const proposedRelationships = [
        ...dataset.productApplicationLinks,
        ...dataset.productProcessLinks,
      ].filter((link) => link.status === 'Proposed').length;

      const queues = [
        {
          queueId: 'assessments',
          label: 'Assessments awaiting review',
          iconToken: 'compass',
          pendingCount: pendingAssessments,
          route: ROUTES.discoverCandidates,
        },
        {
          queueId: 'decisions',
          label: 'Decisions awaiting approval',
          iconToken: 'gavel',
          pendingCount: pendingDecisions,
          route: ROUTES.adminCommandCenter,
        },
        {
          queueId: 'relationships',
          label: 'Proposed relationships',
          iconToken: 'flow',
          pendingCount: proposedRelationships,
          route: ROUTES.mappingProcessProduct,
        },
        {
          queueId: 'mappingSets',
          label: 'Mapping sets in draft',
          iconToken: 'grid',
          pendingCount: draftMappingSets,
          route: ROUTES.mappingProcessProduct,
        },
        {
          queueId: 'conflicts',
          label: 'Source conflicts to resolve',
          iconToken: 'alert',
          pendingCount: openConflicts,
          route: ROUTES.adminCommandCenter,
        },
        {
          queueId: 'quality',
          label: 'Open data quality issues',
          iconToken: 'shield',
          pendingCount: openIssues,
          route: ROUTES.adminLineage,
        },
      ];

      const rules = dataset.dataQualityRules;
      const passed = rules.filter((r) => r.outcome === 'Passed').length;
      const failed = rules.filter((r) => r.outcome === 'Failed').length;
      const waived = rules.filter((r) => r.outcome === 'Waived').length;

      const roleCounts = new Map<string, number>();
      for (const person of dataset.people) {
        const persona = dataset.personas.find((p) => p.code === person.personaCode);
        const label = persona?.name ?? 'Unassigned';
        roleCounts.set(label, (roleCounts.get(label) ?? 0) + 1);
      }

      const view: GovernanceCommandView = {
        kpis: [
          metricValue({
            dataset,
            metricId: 'MET-090',
            label: 'Items Awaiting Action',
            value: queues.reduce((sum, q) => sum + q.pendingCount, 0),
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-091',
            label: 'Data Quality Score',
            value: round((passed / Math.max(1, rules.length)) * 100, 1),
            unit: 'score',
            desiredDirection: 'Up',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-092',
            label: 'Open Conflicts',
            value: openConflicts,
            unit: 'count',
            desiredDirection: 'Down',
            confidence: 'High',
          }),
          metricValue({
            dataset,
            metricId: 'MET-093',
            label: 'Governed Entities',
            value:
              dataset.products.length +
              dataset.applications.length +
              dataset.processes.length +
              dataset.suppliers.length,
            unit: 'count',
            confidence: 'High',
          }),
        ],
        slas: [
          {
            slaId: 'SLA-01',
            name: 'Assessment reviewed within 10 working days',
            target: '10 days',
            achieved: '8.4 days',
            status: 'OnTrack',
          },
          {
            slaId: 'SLA-02',
            name: 'Relationship proposal decided within 5 working days',
            target: '5 days',
            achieved: '6.9 days',
            status: 'AtRisk',
          },
          {
            slaId: 'SLA-03',
            name: 'Source conflict resolved within 15 working days',
            target: '15 days',
            achieved: '21.2 days',
            status: 'Breached',
          },
          {
            slaId: 'SLA-04',
            name: 'Critical data quality issue closed within 30 days',
            target: '30 days',
            achieved: '19.6 days',
            status: 'OnTrack',
          },
        ],
        queues,
        totalPendingItems: queues.reduce((sum, q) => sum + q.pendingCount, 0),
        dataQualityScorecard: {
          overallPercent: round((passed / Math.max(1, rules.length)) * 100, 1),
          buckets: [
            {
              label: 'Passed',
              percent: round(passed / Math.max(1, rules.length), 4),
              colorToken: 'status.positive',
            },
            {
              label: 'Failed',
              percent: round(failed / Math.max(1, rules.length), 4),
              colorToken: 'status.negative',
            },
            {
              label: 'Waived',
              percent: round(waived / Math.max(1, rules.length), 4),
              colorToken: 'status.neutral',
            },
          ],
          totalRules: rules.length,
          passed,
          failed,
          waived,
        },
        roleSummary: {
          totalUsers: dataset.people.length,
          byRole: Array.from(roleCounts.entries())
            .sort((a, b) => b[1] - a[1])
            .map(([role, count]) => ({ role, count })),
        },
        taxonomySummary: [
          { entityType: 'Product', count: dataset.products.length, changeCount: 14 },
          { entityType: 'Application', count: dataset.applications.length, changeCount: 63 },
          { entityType: 'Process', count: dataset.processes.length, changeCount: 8 },
          { entityType: 'ValueStream', count: dataset.valueStreams.length, changeCount: 1 },
          { entityType: 'Supplier', count: dataset.suppliers.length, changeCount: 2 },
          { entityType: 'Contract', count: dataset.contracts.length, changeCount: 5 },
        ],
        stewardshipTasks: [
          { label: 'Applications without an owner', count: 0, isOverdue: false },
          {
            label: 'Applications with no product mapping',
            count:
              dataset.applications.length -
              new Set(dataset.productApplicationLinks.map((l) => l.applicationCode)).size,
            isOverdue: true,
          },
          {
            label: 'Cost actuals with no allocation',
            count:
              dataset.costActuals.length -
              new Set(dataset.costAllocations.map((a) => a.costActualCode)).size,
            isOverdue: true,
          },
          {
            label: 'Relationships with low confidence',
            count: dataset.productApplicationLinks.filter((l) => l.confidenceBand === 'Low').length,
            isOverdue: false,
          },
          {
            label: 'Evidence older than 12 months',
            count: dataset.evidence.filter(
              (e) => e.date !== null && daysBetween(e.date, dataset.asOf) > 365,
            ).length,
            isOverdue: false,
          },
        ],
        approvalBreakdown: [
          {
            status: 'Approved',
            count: dataset.assessments.filter((a) => a.status === 'Approved').length,
            colorToken: 'status.positive',
          },
          {
            status: 'In review',
            count: dataset.assessments.filter((a) => a.status === 'InReview').length,
            colorToken: 'status.warning',
          },
          {
            status: 'Submitted',
            count: dataset.assessments.filter((a) => a.status === 'Submitted').length,
            colorToken: 'status.info',
          },
          {
            status: 'Changes requested',
            count: dataset.assessments.filter((a) => a.status === 'ChangesRequested').length,
            colorToken: 'status.negative',
          },
          {
            status: 'Draft',
            count: dataset.assessments.filter((a) => a.status === 'Draft').length,
            colorToken: 'status.neutral',
          },
        ],
        auditTrail: dataset.auditEvents.slice(0, 25).map((event) => ({
          id: event.id,
          occurredAt: event.occurredAt,
          actor: index.peopleByCode.get(event.actorPersonCode)?.displayName ?? 'System',
          action: event.action,
          entityLabel: event.entityLabel,
          detail: event.detail,
          correlationId: event.correlationId,
        })),
        dataQualityExceptions: dataset.dataQualityIssues
          .filter((issue) => issue.status === 'Open' || issue.status === 'InProgress')
          .slice(0, 12)
          .map((issue) => ({
            severity: issue.severity,
            ruleCode: issue.ruleCode,
            entityLabel: issue.entityLabel,
            issue: issue.issue,
            ageLabel: `${daysBetween(issue.raisedOn, dataset.asOf)} days`,
          })),
        integrationAlerts: dataset.connectors
          .filter((connector) => connector.connectionHealth !== 'Healthy')
          .map((connector) => ({
            system: connector.name,
            status:
              connector.connectionHealth === 'Down' ? ('Error' as const) : ('Warning' as const),
            alert:
              connector.connectionHealth === 'Down'
                ? 'Authentication failed; last successful run is being served.'
                : connector.connectionHealth === 'Paused'
                  ? 'Paused pending credential rotation.'
                  : 'Elevated error queue and delayed refresh.',
            sinceLabel: connector.lastSyncAt ? `since ${connector.lastSyncAt.slice(0, 10)}` : null,
          })),
        configuration: [
          {
            label: 'Taxonomy & vocabularies',
            iconToken: 'list',
            summary: 'Controlled values for lifecycle, classification and criticality.',
            route: ROUTES.adminCommandCenter,
          },
          {
            label: 'Metric catalogue',
            iconToken: 'gauge',
            summary: 'Governed metric definitions and their versions.',
            route: ROUTES.adminCommandCenter,
          },
          {
            label: 'Roles & entitlements',
            iconToken: 'shield',
            summary: '24 personas mapped to modules, scopes and field policies.',
            route: ROUTES.adminCommandCenter,
          },
          {
            label: 'Data sources',
            iconToken: 'plug',
            summary: '12 registered sources with declared refresh frequencies.',
            route: ROUTES.adminIntegrations,
          },
        ],
        notificationRules: [
          { label: 'Source failure', iconToken: 'alert', activeCount: 4 },
          { label: 'Approval requested', iconToken: 'inbox', activeCount: 11 },
          { label: 'Quality threshold breached', iconToken: 'shield', activeCount: 6 },
          { label: 'Contract notice period', iconToken: 'contract', activeCount: 3 },
        ],
        release: {
          nextWindow: '2026-06-13',
          nextWindowLabel: 'Saturday 13 June, 02:00–06:00 UTC',
          inProgress: 'Mapping set MAP-2026-Q3 validation',
          changeRequests: 7,
          health: openConflicts > 2 ? 'Degraded' : 'Healthy',
        },
      };

      return resolved({
        data: view,
        meta: buildMetadata(dataset, user, scope, {
          route: ROUTES.adminCommandCenter,
          sources: PAGE_SOURCES.governance,
          confidence: 'High',
          actions: [
            {
              action: 'resolveConflict',
              permission: 'approve',
              enabled: user.allowedModules.includes('governance'),
              disabledReason: user.allowedModules.includes('governance')
                ? undefined
                : 'Conflict resolution is reserved for data stewards.',
            },
            { action: 'export', permission: 'export', enabled: true },
          ],
        }),
      });
    },
  };
}
